import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { AUDIT_DATE_PRESETS, type AuditDatePreset } from '@sdr-agend/admin-schemas'

/**
 * Subset of `auditAction` enum exposed in the filter dropdown. We don't have
 * a registry of "all action names" in the admin-schemas (the API audit union
 * type lists ~80 values but isn't exported as a runtime array), so this list
 * captures the ones most relevant to investigation. Empty string = "any".
 *
 * Adding actions here is cheap; missing ones can still be queried via the
 * `Recurso` text filter (substring match on resourceType) or by typing the
 * full action manually via deep-link.
 */
export const COMMON_ACTIONS: ReadonlyArray<string> = [
  'prompt.activated',
  'prompt.updated',
  'prompt.rolled_back',
  'prompt.deleted',
  'campaign.created',
  'campaign.updated',
  'campaign.activated',
  'campaign.deleted',
  'seller.created',
  'seller.updated',
  'seller.activated',
  'seller.deactivated',
  'system_setting.updated',
  'user.login_success',
  'user.login_failed',
  'user.created',
  'user.updated',
  'meeting.created',
  'meeting.cancelled',
  'meeting.rescheduled',
  'meeting.outcome_set',
  'opt_out.detected',
  'opt_out.applied',
  'handoff.triggered',
  'handoff.released_manual',
  'webhook.received',
  'webhook.signature_invalid',
]

export interface AuditFilterValues {
  action: string
  resourceType: string
  actorUserId: string
  preset: AuditDatePreset | ''
  from: string
  to: string
}

interface AuditFiltersProps {
  values: AuditFilterValues
  onChange: (next: AuditFilterValues) => void
  actors: ReadonlyArray<{ id: string; email: string }>
}

/**
 * Filter bar for `/admin/audit-log`.
 *
 * Story 3.17 §AC #5: Ação (combobox), Recurso (text), Ator (combobox),
 * Date range (preset 1h / 24h / 7d / 30d + custom from/to).
 */
export function AuditFilters({
  values,
  onChange,
  actors,
}: AuditFiltersProps): React.ReactElement {
  function update<K extends keyof AuditFilterValues>(
    key: K,
    next: AuditFilterValues[K],
  ): void {
    onChange({ ...values, [key]: next })
  }

  function applyPreset(preset: AuditDatePreset): void {
    const to = new Date()
    const from = new Date(to)
    switch (preset) {
      case '1h':
        from.setHours(from.getHours() - 1)
        break
      case '24h':
        from.setDate(from.getDate() - 1)
        break
      case '7d':
        from.setDate(from.getDate() - 7)
        break
      case '30d':
        from.setDate(from.getDate() - 30)
        break
    }
    onChange({
      ...values,
      preset,
      from: from.toISOString(),
      to: to.toISOString(),
    })
  }

  function clearDates(): void {
    onChange({ ...values, preset: '', from: '', to: '' })
  }

  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
      <div className="space-y-1">
        <Label htmlFor="audit-action">Ação</Label>
        <Select
          id="audit-action"
          value={values.action}
          onChange={(e) => update('action', e.target.value)}
        >
          <option value="">Todas</option>
          {COMMON_ACTIONS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </Select>
      </div>

      <div className="space-y-1">
        <Label htmlFor="audit-resource">Recurso (tipo)</Label>
        <Input
          id="audit-resource"
          value={values.resourceType}
          placeholder="ex: prompt, campaign, seller…"
          onChange={(e) => update('resourceType', e.target.value)}
        />
      </div>

      <div className="space-y-1">
        <Label htmlFor="audit-actor">Ator</Label>
        <Select
          id="audit-actor"
          value={values.actorUserId}
          onChange={(e) => update('actorUserId', e.target.value)}
        >
          <option value="">Todos</option>
          {actors.map((a) => (
            <option key={a.id} value={a.id}>
              {a.email}
            </option>
          ))}
        </Select>
      </div>

      <div className="space-y-1">
        <Label>Período</Label>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Filtrar por período">
          {AUDIT_DATE_PRESETS.map((p) => (
            <Button
              key={p}
              type="button"
              size="sm"
              variant={values.preset === p ? 'default' : 'outline'}
              onClick={() => applyPreset(p)}
              aria-pressed={values.preset === p}
            >
              {p}
            </Button>
          ))}
          {(values.preset !== '' || values.from !== '' || values.to !== '') && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={clearDates}
              aria-label="Limpar período"
            >
              limpar
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default AuditFilters
